import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { portalApi, resolveFileUrl, isStoredFileUrl } from '../../../../lib/portalApi';
import { useAuth } from '../../../../context/AuthContext';
import { QuestionField, DocumentField } from '../../shared/DynamicFormFields';
import KanbanBoard, { statusChip } from '../../shared/KanbanBoard';
import BatchTaskDetailPanel from '../../shared/BatchTaskDetailPanel';
import SearchableStartupDocuments from '../../shared/SearchableStartupDocuments';
import { CheckCircle, AlertCircle, Clock, Lock, FileText, Download, ExternalLink, Save, Send } from 'lucide-react';

export function FounderOverviewPage() {
  const { user } = useAuth();
  const s = user?.startups?.[0];
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-white">Welcome{user?.fullName ? `, ${user.fullName}` : ''}</h2>
        <p className="text-white/50 mt-1">
          {s ? (
            <>
              <span className="text-white">{s.name}</span> · stage <span className="text-[#ff8ec5]">{s.stage}</span>
            </>
          ) : (
            'Your founder portal'
          )}
        </p>
      </div>
      {user?.mustChangePassword && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-amber-200 text-sm">
          Please <Link to="/founder/settings" className="underline">change your temporary password</Link>.
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        {s?.stage === 'accepted' && (
          <Link to="/founder/onboarding" className="px-4 py-2 rounded-xl bg-[#d42b92] text-white text-sm">
            Continue onboarding
          </Link>
        )}
        {(s?.stage === 'active_batch' || s?.skipOnboarding) && (
          <Link to="/founder/batch" className="px-4 py-2 rounded-xl bg-[#d42b92] text-white text-sm">
            Open Batch OS
          </Link>
        )}
        <Link to="/founder/company-profile" className="px-4 py-2 rounded-xl border border-white/15 text-white text-sm">
          Company profile
        </Link>
        <Link to="/founder/documents" className="px-4 py-2 rounded-xl border border-white/15 text-white text-sm hover:border-[#d42b92]/50 transition-colors">
          Documents
        </Link>
      </div>
    </div>
  );
}

export function FounderApplicationPage() {
  const [apps, setApps] = useState([]);
  useEffect(() => {
    portalApi.myApplications().then(setApps).catch(() => setApps([]));
  }, []);
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-semibold text-white">Application</h2>
      {apps.length === 0 && <p className="text-white/40">No application on file.</p>}
      {apps.map((a) => (
        <div key={a.id} className="rounded-xl border border-white/10 px-4 py-3">
          <div className="text-white font-medium">{a.companyName}</div>
          <div className="text-sm text-[#ff8ec5] capitalize">{a.status?.replace('_', ' ')}</div>
        </div>
      ))}
    </div>
  );
}

function icsHref(meeting) {
  if (!meeting?.scheduledAt) return null;
  const start = new Date(meeting.scheduledAt);
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  const fmt = (d) =>
    d
      .toISOString()
      .replace(/[-:]/g, '')
      .replace(/\.\d{3}/, '');
  const desc = [meeting.meetingLink, meeting.location, meeting.notes].filter(Boolean).join('\\n');
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'BEGIN:VEVENT',
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:${(meeting.title || 'Meeting').replace(/,/g, '\\,')}`,
    desc ? `DESCRIPTION:${desc}` : '',
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .filter(Boolean)
    .join('\n');
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

function googleCalHref(meeting) {
  if (!meeting?.scheduledAt) return null;
  const start = new Date(meeting.scheduledAt);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: meeting.title || 'Meeting',
    dates: `${fmt(start)}/${fmt(end)}`,
    details: [meeting.meetingLink, meeting.location].filter(Boolean).join('\n'),
  });
  return `https://calendar.google.com/calendar/render?${params}`;
}

async function downloadAuthed(url, filename) {
  const token = localStorage.getItem('innovher_token');
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename || 'document';
  a.click();
  URL.revokeObjectURL(a.href);
}

export function FounderOnboardingBoardPage() {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState('');
  const [signed, setSigned] = useState({});
  const [comments, setComments] = useState({});
  const [actionTitles, setActionTitles] = useState({});

  const load = () =>
    portalApi
      .founderOnboarding()
      .then(setBoard)
      .catch((e) => setError(e.message));

  useEffect(() => {
    load();
  }, []);

  const run = async (fn) => {
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  if (error) return <p className="text-red-400">{error}</p>;
  if (!board) return <p className="text-white/40">Loading…</p>;

  const steps = board.steps || [];
  const stepById = Object.fromEntries(steps.map((s) => [s.id, s]));
  const percent = board.percent ?? 0;
  const kanban = board.kanban || {};
  const timeline = board.timeline || [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-white">Onboarding</h2>
        <p className="text-white/50 text-sm">{board.startup?.name}</p>
        <div className="mt-3 flex items-center gap-3">
          <div className="h-1.5 w-40 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-[#d42b92]" style={{ width: `${percent}%` }} />
          </div>
          <span className="text-sm text-[#ff8ec5]">{percent}%</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {steps.map((step) => (
          <div key={step.id} className="rounded-2xl border border-white/10 p-4 space-y-3">
            <div className="flex justify-between gap-2">
              <div>
                <div className="text-white font-medium">{step.title}</div>
                {step.description && (
                  <div className="text-xs text-white/40 mt-0.5">{step.description}</div>
                )}
                {step.deadline && (
                  <div className="text-xs text-[#ff8ec5] mt-1">
                    Deadline · {new Date(step.deadline).toLocaleString()}
                  </div>
                )}
              </div>
              <span className="text-xs text-[#ff8ec5] shrink-0">{step.status}</span>
            </div>

            {step.sentDocuments?.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 text-sm text-white/70">
                <span>
                  Doc: {d.title} {d.seenAt ? '· seen' : ''}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="text-[#ff8ec5] text-xs"
                    onClick={async () => {
                      try {
                        await downloadAuthed(
                          portalApi.downloadSentDocUrl(d.id),
                          d.originalName || d.title
                        );
                        load();
                      } catch (e) {
                        setError(e.message);
                      }
                    }}
                  >
                    Download
                  </button>
                  {!d.seenAt && (
                    <button
                      type="button"
                      onClick={() => portalApi.markDocSeen(d.id).then(load)}
                      className="text-[#ff8ec5] text-xs"
                    >
                      Mark seen
                    </button>
                  )}
                </div>
              </div>
            ))}

            {step.documentRequests?.map((d) => (
              <div key={d.id} className="space-y-2 text-sm">
                <div className="text-white/70">
                  Upload: {d.title} · {d.status}
                  {d.instructions ? `, ${d.instructions}` : ''}
                </div>
                {d.submittedUrl && (
                  <a
                    href={resolveFileUrl(d.submittedUrl)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-[#ff8ec5]"
                  >
                    Your upload: {d.submittedOriginalName || 'Open file'}
                  </a>
                )}
                {['requested', 'rejected'].includes(d.status) && (
                  <div className="space-y-2">
                    {d.requiresSignature && (
                      <label className="flex items-center gap-2 text-xs text-white/60">
                        <input
                          type="checkbox"
                          checked={!!signed[d.id]}
                          onChange={(e) => setSigned({ ...signed, [d.id]: e.target.checked })}
                        />
                        I confirm this document is signed
                      </label>
                    )}
                    <input
                      type="file"
                      className="text-xs text-white/50"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (d.requiresSignature && !signed[d.id]) {
                          setError('Please confirm signature before uploading');
                          e.target.value = '';
                          return;
                        }
                        const fd = new FormData();
                        fd.append('file', file);
                        if (d.requiresSignature) fd.append('signed', 'true');
                        try {
                          setError('');
                          await portalApi.submitDocRequest(d.id, fd);
                          load();
                        } catch (err) {
                          setError(err.message || 'Upload failed');
                          e.target.value = '';
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            ))}

            {step.meetings?.map((m) => {
              const gcal = googleCalHref(m);
              const ics = icsHref(m);
              return (
                <div key={m.id} className="text-xs text-white/50 space-y-1">
                  <div>
                    Meeting: {m.title}
                    {m.scheduledAt ? ` · ${new Date(m.scheduledAt).toLocaleString()}` : ''}
                    {m.meetingLink ? (
                      <>
                        {' '}
                        ·{' '}
                        <a href={m.meetingLink} className="text-[#ff8ec5]" target="_blank" rel="noreferrer">
                          join
                        </a>
                      </>
                    ) : null}
                  </div>
                  <div className="flex gap-3">
                    {gcal && (
                      <a href={gcal} target="_blank" rel="noreferrer" className="text-[#ff8ec5]">
                        Google Calendar
                      </a>
                    )}
                    {ics && (
                      <a href={ics} download={`${m.title || 'meeting'}.ics`} className="text-[#ff8ec5]">
                        .ics
                      </a>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Action items */}
            <div className="border-t border-white/10 pt-3 space-y-2">
              <div className="text-xs uppercase tracking-wide text-white/40">Action items</div>
              {(step.actionItems || []).map((a) => (
                <label key={a.id} className="flex items-center gap-2 text-sm text-white/70">
                  <input
                    type="checkbox"
                    checked={!!a.done}
                    onChange={() => run(() => portalApi.toggleActionItem(a.id))}
                  />
                  <span className={a.done ? 'line-through opacity-50' : ''}>{a.title}</span>
                </label>
              ))}
              <div className="flex gap-2">
                <input
                  value={actionTitles[step.id] || ''}
                  onChange={(e) =>
                    setActionTitles({ ...actionTitles, [step.id]: e.target.value })
                  }
                  placeholder="Add action item…"
                  className="flex-1 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                />
                <button
                  type="button"
                  className="text-xs text-[#ff8ec5]"
                  onClick={() =>
                    run(async () => {
                      const title = (actionTitles[step.id] || '').trim();
                      if (!title) return;
                      await portalApi.addActionItem(step.id, { title });
                      setActionTitles({ ...actionTitles, [step.id]: '' });
                    })
                  }
                >
                  Add
                </button>
              </div>
            </div>

            {/* Comments */}
            <div className="border-t border-white/10 pt-3 space-y-2">
              <div className="text-xs uppercase tracking-wide text-white/40">
                Comments ({(step.comments || []).length})
              </div>
              {(step.comments || []).map((c) => (
                <div key={c.id} className="text-xs text-white/60">
                  <span className="text-white/35">{c.author || 'Staff'}: </span>
                  {c.body}
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  value={comments[step.id] || ''}
                  onChange={(e) => setComments({ ...comments, [step.id]: e.target.value })}
                  placeholder="Add a comment…"
                  className="flex-1 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                />
                <button
                  type="button"
                  className="text-xs text-[#ff8ec5]"
                  onClick={() =>
                    run(async () => {
                      const body = (comments[step.id] || '').trim();
                      if (!body) return;
                      await portalApi.addComment(step.id, { body });
                      setComments({ ...comments, [step.id]: '' });
                    })
                  }
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-4 gap-3">
        {[
          ['pending', 'Pending'],
          ['in_progress', 'In progress'],
          ['waiting', 'Waiting'],
          ['completed', 'Completed'],
        ].map(([key, label]) => (
          <div key={key} className="rounded-xl border border-white/10 p-3 min-h-[100px]">
            <div className="text-[10px] uppercase tracking-wide text-white/40 mb-2">{label}</div>
            {(kanban[key] || []).map((sid) => (
              <div key={sid} className="text-sm text-white/70 py-1">
                {stepById[sid]?.title || sid}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-white/10 p-4">
        <h3 className="text-white font-medium mb-2">Timeline</h3>
        {timeline.length === 0 && (
          <p className="text-sm text-white/40">No completed or cancelled steps yet</p>
        )}
        {timeline.map((s) => (
          <div key={s.id} className="text-sm text-white/60 py-1">
            {s.title} · {s.status}
          </div>
        ))}
      </div>
    </div>
  );
}

export function FounderCompanyProfilePage() {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [qFiles, setQFiles] = useState({});
  const [docFiles, setDocFiles] = useState({});
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const load = () =>
    portalApi
      .myCompanyProfile()
      .then((d) => {
        setData(d);
        setAnswers(d.startup?.profileAnswers || {});
      })
      .catch((e) => setError(e.message));

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const fd = new FormData();
      const clean = { ...answers };
      Object.keys(clean).forEach((k) => {
        if (k.endsWith('__meta')) delete clean[k];
      });
      fd.append('answers', JSON.stringify(clean));
      Object.entries(docFiles).forEach(([id, file]) => {
        if (file) fd.append(`doc_${id}`, file);
      });
      Object.entries(qFiles).forEach(([id, file]) => {
        if (file) fd.append(`qfile_${id}`, file);
      });
      await portalApi.saveCompanyProfile(fd);
      setMsg('Saved');
      setQFiles({});
      setDocFiles({});
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('logo', file);
    await portalApi.uploadLogo(fd);
    setMsg('Logo uploaded');
    load();
  };

  if (!data && !error) return <p className="text-white/40">Loading…</p>;

  const stages = data?.form?.stages || [];
  const questions = data?.form?.questions || [];
  const docs = data?.form?.documentRequirements || [];
  const profileDocs = data?.startup?.profileDocuments || [];

  return (
    <div className="space-y-6 max-w-xl">
      <h2 className="text-2xl font-semibold text-white">Company profile</h2>
      {msg && <p className="text-[#ff8ec5] text-sm">{msg}</p>}
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <div>
        <label className="text-xs text-white/45 uppercase tracking-wide">Logo</label>
        <input type="file" accept="image/*" onChange={uploadLogo} className="mt-2 block text-sm text-white/60" />
        {data?.startup?.logo && (
          <img
            src={resolveFileUrl(data.startup.logo)}
            alt="logo"
            className="mt-3 h-16 w-16 object-contain rounded-lg bg-white/5"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
      </div>
      <form onSubmit={save} className="space-y-6">
        {stages.length > 0
          ? stages.map((s) => {
              const qs = (s.questions || questions.filter((q) => q.stageId === s.id)).filter(
                (q) => q.active !== false
              );
              const ds = (s.documentRequirements || docs.filter((d) => d.stageId === s.id)).filter(
                (d) => d.active !== false
              );
              return (
                <div key={s.id} className="space-y-4">
                  <h3 className="text-white font-medium border-b border-white/10 pb-2">{s.title}</h3>
                  {qs.map((q) => (
                    <QuestionField
                      key={q.id}
                      question={q}
                      value={answers[q.id]}
                      existingUrl={isStoredFileUrl(answers[q.id]) ? answers[q.id] : null}
                      onChange={(v) => setAnswers({ ...answers, [q.id]: v })}
                      onFile={(f) => setQFiles({ ...qFiles, [q.id]: f })}
                    />
                  ))}
                  {ds.map((d) => (
                    <DocumentField
                      key={d.id}
                      doc={d}
                      existing={profileDocs.find((x) => String(x.requirementId) === String(d.id))}
                      onFile={(f) => setDocFiles({ ...docFiles, [d.id]: f })}
                    />
                  ))}
                </div>
              );
            })
          : questions.map((q) => (
              <QuestionField
                key={q.id}
                question={q}
                value={answers[q.id]}
                onChange={(v) => setAnswers({ ...answers, [q.id]: v })}
                onFile={(f) => setQFiles({ ...qFiles, [q.id]: f })}
              />
            ))}
        <button type="submit" className="px-4 py-2 rounded-xl bg-[#d42b92] text-white text-sm">
          Save profile
        </button>
      </form>
    </div>
  );
}

export function FounderDdPage() {
  const [dd, setDd] = useState(null);
  const [answers, setAnswers] = useState({});
  const [qFiles, setQFiles] = useState({});
  const [docFiles, setDocFiles] = useState({});
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [activeStageId, setActiveStageId] = useState('ALL');

  const load = () =>
    portalApi
      .founderDd()
      .then((d) => {
        setDd(d);
        setAnswers(d.answers || {});
      })
      .catch((e) => setMsg(e.message));

  useEffect(() => {
    load();
  }, []);

  const saveOrSubmit = async (isSubmit) => {
    setError('');
    setMsg('');
    setBusy(true);
    try {
      const fd = new FormData();
      const clean = { ...answers };
      Object.keys(clean).forEach((k) => {
        if (k.endsWith('__meta')) delete clean[k];
      });
      fd.append('answers', JSON.stringify(clean));
      if (isSubmit) {
        fd.append('submit', 'true');
      }
      Object.entries(docFiles).forEach(([id, file]) => {
        if (file) fd.append(`doc_${id}`, file);
      });
      Object.entries(qFiles).forEach(([id, file]) => {
        if (file) fd.append(`qfile_${id}`, file);
      });
      await portalApi.submitDd(fd);
      setMsg(
        isSubmit
          ? 'Due Diligence submitted successfully! Your submission is now under review.'
          : 'Draft saved successfully.'
      );
      await load();
      setQFiles({});
      setDocFiles({});
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const stages = useMemo(() => dd?.stages || [], [dd]);

  const displayedQuestions = useMemo(() => {
    const all = dd?.questions || [];
    if (activeStageId === 'ALL') return all;
    return all.filter((q) => String(q.stageId) === String(activeStageId));
  }, [dd, activeStageId]);

  const displayedDocs = useMemo(() => {
    const all = dd?.documentRequirements || [];
    if (activeStageId === 'ALL') return all;
    return all.filter((d) => String(d.stageId) === String(activeStageId));
  }, [dd, activeStageId]);

  if (!dd) return <p className="text-white/40">{msg || 'Loading due diligence questionnaire…'}</p>;
  if (dd.status === 'closed') {
    return (
      <div className="space-y-4 max-w-2xl">
        <h2 className="text-2xl font-semibold text-white">Due Diligence</h2>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center space-y-2">
          <p className="text-white/60">No open due diligence request at this time.</p>
          <p className="text-xs text-white/35">
            Your accelerator director or program lead will open DD when required.
          </p>
        </div>
      </div>
    );
  }

  const isSubmitted = dd.status === 'submitted';
  const isUnderReview = dd.status === 'under_review';

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-white">Due Diligence Workspace</h2>
          <p className="text-white/45 text-sm mt-0.5">
            Complete required information and upload verification documents for your accelerator cohort.
          </p>
        </div>
        <div>
          {isSubmitted ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-medium">
              <CheckCircle className="w-3.5 h-3.5" />
              Submitted · Under Review
            </span>
          ) : isUnderReview ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-300 text-xs font-medium">
              <Clock className="w-3.5 h-3.5" />
              Under Review
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#d42b92]/10 border border-[#d42b92]/30 text-[#ff8ec5] text-xs font-medium">
              <Clock className="w-3.5 h-3.5" />
              Open for Submission
            </span>
          )}
        </div>
      </div>

      {isSubmitted && (
        <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="text-emerald-300 font-medium">Due diligence submitted</div>
            <div className="text-emerald-400/80 text-xs mt-0.5">
              {dd.submittedAt
                ? `Submitted on ${new Date(dd.submittedAt).toLocaleDateString()} at ${new Date(dd.submittedAt).toLocaleTimeString()}. `
                : ''}
              The accelerator investment committee is reviewing your submission. You can continue updating draft answers or upload additional documents if requested.
            </div>
          </div>
        </div>
      )}

      {msg && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-emerald-300 text-sm flex items-center gap-2">
          <CheckCircle className="w-4 h-4 shrink-0" />
          <span>{msg}</span>
        </div>
      )}
      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-300 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Stage tabs if stages exist */}
      {stages.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-white/10 pb-3">
          <button
            type="button"
            onClick={() => setActiveStageId('ALL')}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
              activeStageId === 'ALL'
                ? 'border-[#d42b92] bg-[#d42b92]/20 text-white shadow-sm shadow-[#d42b92]/20 font-medium'
                : 'border-white/10 text-white/50 hover:text-white/80'
            }`}
          >
            All Sections ({(dd.questions || []).length} Qs)
          </button>
          {stages.map((s, idx) => {
            const qCount = (dd.questions || []).filter((q) => q.stageId === s.id).length;
            const dCount = (dd.documentRequirements || []).filter((d) => d.stageId === s.id).length;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveStageId(s.id)}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                  activeStageId === s.id
                    ? 'border-[#d42b92] bg-[#d42b92]/20 text-white shadow-sm shadow-[#d42b92]/20 font-medium'
                    : 'border-white/10 text-white/50 hover:text-white/80'
                }`}
              >
                Stage {idx + 1}: {s.title} ({qCount} Qs{dCount ? ` · ${dCount} docs` : ''})
              </button>
            );
          })}
        </div>
      )}

      {/* Questions & Documents Form */}
      <div className="space-y-6">
        {displayedQuestions.length > 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.015] p-5 space-y-4">
            <h3 className="text-white font-medium text-base">Questionnaire</h3>
            <div className="space-y-4">
              {displayedQuestions.map((q) => (
                <QuestionField
                  key={q.id}
                  question={q}
                  value={answers[q.id]}
                  existingUrl={isStoredFileUrl(answers[q.id]) ? answers[q.id] : null}
                  onChange={(v) => setAnswers({ ...answers, [q.id]: v })}
                  onFile={(f) => setQFiles({ ...qFiles, [q.id]: f })}
                />
              ))}
            </div>
          </div>
        )}

        {displayedDocs.length > 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.015] p-5 space-y-4">
            <h3 className="text-white font-medium text-base">Required Documents</h3>
            <div className="space-y-4">
              {displayedDocs.map((d) => (
                <DocumentField
                  key={d.id}
                  doc={d}
                  existing={(dd.documents || []).find((x) => String(x.requirementId) === String(d.id))}
                  onFile={(f) => setDocFiles({ ...docFiles, [d.id]: f })}
                />
              ))}
            </div>
          </div>
        )}

        {displayedQuestions.length === 0 && displayedDocs.length === 0 && (
          <div className="rounded-2xl border border-dashed border-white/15 p-8 text-center text-white/40 text-sm">
            No questions or document requirements in this section.
          </div>
        )}

        {/* Action Controls */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <div className="text-xs text-white/40">
            {isSubmitted ? 'Last submitted · changes will update the review package.' : 'Changes are saved to draft until submitted.'}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => saveOrSubmit(false)}
              className="px-4 py-2.5 rounded-xl border border-white/15 hover:border-white/30 text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4 text-white/60" />
              Save Draft
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => saveOrSubmit(true)}
              className="px-5 py-2.5 rounded-xl bg-[#d42b92] hover:bg-[#d42b92]/90 text-white text-sm font-medium transition-colors shadow-lg shadow-[#d42b92]/25 disabled:opacity-50 flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              {isSubmitted ? 'Update & Re-Submit' : 'Submit Due Diligence'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function FounderBatchPage() {
  const [tasks, setTasks] = useState([]);
  const [meetings, setMeetings] = useState([]);
  useEffect(() => {
    portalApi.myBatchTasks().then(setTasks).catch(() => setTasks([]));
    portalApi.myMeetings().then(setMeetings).catch(() => setMeetings([]));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-white">Batch OS</h2>
        <p className="text-white/50 text-sm">Active batch tasks and milestones.</p>
      </div>
      <div className="flex flex-wrap gap-4">
        <Link to="/founder/batch/tasks" className="text-sm text-[#ff8ec5]">
          View all tasks →
        </Link>
        <Link to="/founder/meetings" className="text-sm text-[#ff8ec5]">
          Meetings →
        </Link>
      </div>
      <div className="space-y-2">
        {tasks.length === 0 && <p className="text-white/40">No tasks yet.</p>}
        {tasks.slice(0, 8).map((t) => {
          const isDaily = t.kind === 'daily' || t.isDaily;
          const assignedByText =
            t.assignedByName ||
            t.assignedBy?.name ||
            (t.assignedById ? 'Administrator' : 'Not recorded');

          return (
            <div key={t.id} className="rounded-xl border border-white/10 px-4 py-3 flex justify-between gap-2">
              <div>
                <div className="text-white flex items-center gap-2 flex-wrap">
                  {t.code ? <span className="text-white/40">{t.code} ·</span> : null}
                  <span>{t.title}</span>
                  {isDaily && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#d42b92]/20 text-[#ff8ec5] border border-[#d42b92]/40">
                      Daily
                    </span>
                  )}
                </div>
                <div className="text-xs text-white/40 mt-0.5">
                  {isDaily ? `Daily task · Assigned by: ${assignedByText}` : t.kind}
                  {t.taskDate && isDaily ? ` · Date: ${new Date(t.taskDate).toLocaleDateString()}` : ''}
                </div>
              </div>
              <span className="text-xs text-[#ff8ec5] shrink-0">{t.status}</span>
            </div>
          );
        })}
      </div>
      {meetings.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-white font-medium">Upcoming meetings</h3>
          {meetings.slice(0, 5).map((m) => (
            <div key={`${m.source}-${m.id}`} className="text-sm text-white/60">
              {m.title}
              {m.scheduledAt ? ` · ${new Date(m.scheduledAt).toLocaleString()}` : ''}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FounderBatchTasksPage() {
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState('');
  const [signed, setSigned] = useState({});
  const [view, setView] = useState('kanban');
  const [filter, setFilter] = useState('all');
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const todayKolkataYMD = () =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());

  const load = () => portalApi.myBatchTasks().then(setTasks).catch(() => setTasks([]));
  useEffect(() => {
    load();
  }, []);

  const run = async (fn) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const filteredTasks = useMemo(() => {
    if (filter === 'all') return tasks;
    const today = todayKolkataYMD();
    const now = new Date();

    if (filter === 'today') {
      return tasks.filter((t) => {
        if (t.taskDate) {
          const td = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Asia/Kolkata',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }).format(new Date(t.taskDate));
          if (td === today) return true;
        }
        if (t.deadline || t.dueAt) {
          const dd = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Asia/Kolkata',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }).format(new Date(t.deadline || t.dueAt));
          if (dd === today) return true;
        }
        return false;
      });
    }

    if (filter === 'overdue') {
      return tasks.filter((t) => {
        if (['completed', 'cancelled'].includes(t.status)) return false;
        const due = t.deadline || t.dueAt;
        if (!due) return false;
        return new Date(due).getTime() < now.getTime();
      });
    }

    if (filter === 'daily') {
      return tasks.filter((t) => t.kind === 'daily' || t.isDaily);
    }

    if (filter === 'milestones') {
      return tasks.filter((t) => t.kind === 'milestone');
    }

    return tasks;
  }, [tasks, filter]);

  const colId = (status) => {
    if (status === 'todo') return 'todo';
    if (status === 'in_progress') return 'in_progress';
    if (['clarification', 'blocked', 'on_hold'].includes(status)) return 'waiting';
    if (['ready_for_review', 'rework'].includes(status)) return 'review';
    return 'done';
  };

  const moveFounder = async (task, to) => {
    const s = task.status;
    if (colId(s) === to) return;
    const targetByColumn = {
      todo: 'todo',
      in_progress: 'in_progress',
      waiting: 'on_hold',
      review: 'ready_for_review',
      done: 'completed',
    };
    const next = targetByColumn[to];
    if (!next || next === s) return;
    const reason =
      to === 'waiting'
        ? 'Paused from board'
        : to === 'todo' && ['completed', 'cancelled'].includes(s)
          ? 'Reopened from board'
          : undefined;
    await portalApi.transitionTask(task.id, next, reason);
  };

  const columns = [
    { id: 'todo', title: 'To do' },
    { id: 'in_progress', title: 'In progress' },
    { id: 'waiting', title: 'Waiting' },
    { id: 'review', title: 'Review' },
    { id: 'done', title: 'Done' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-white">Batch tasks</h2>
          <p className="text-sm text-white/45 mt-1">
            Open a task for to-dos and comments. Drag cards to update status.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setView('list')}
            className={`text-xs px-2.5 py-1 rounded-lg border ${
              view === 'list' ? 'border-[#d42b92] text-white' : 'border-white/15 text-white/50'
            }`}
          >
            List
          </button>
          <button
            type="button"
            onClick={() => setView('kanban')}
            className={`text-xs px-2.5 py-1 rounded-lg border ${
              view === 'kanban' ? 'border-[#d42b92] text-white' : 'border-white/15 text-white/50'
            }`}
          >
            Kanban
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {[
          ['all', 'All tasks'],
          ['today', 'Today'],
          ['overdue', 'Overdue'],
          ['daily', 'Daily only'],
          ['milestones', 'Milestones only'],
        ].map(([f, label]) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`text-xs px-2.5 py-1 rounded-lg border transition ${
              filter === f
                ? 'border-[#d42b92] bg-[#d42b92]/20 text-white'
                : 'border-white/10 text-white/50 hover:text-white/80'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}
      {busy && (
        <p className="text-xs text-[#ff8ec5]/80" aria-live="polite">
          Please wait, request in progress…
        </p>
      )}

      {view === 'kanban' && (
        <KanbanBoard
          columns={columns}
          disabled={busy}
          className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
          items={filteredTasks.map((t) => ({ ...t, columnId: colId(t.status) }))}
          renderCard={(t) => {
            const isDaily = t.kind === 'daily' || t.isDaily;
            const assignedByText =
              t.assignedByName ||
              t.assignedBy?.name ||
              (t.assignedById ? 'Administrator' : 'Not recorded');

            return (
              <button
                type="button"
                className="w-full text-left space-y-1.5"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTaskId(t.id);
                }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <div className="text-sm text-white font-medium">
                  {t.code ? <span className="text-white/35 mr-1">{t.code}</span> : null}
                  {t.title}
                </div>
                <div className="flex gap-1.5 items-center flex-wrap">
                  {statusChip(t.status)}
                  {isDaily ? (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#d42b92]/20 text-[#ff8ec5] border border-[#d42b92]/40">
                      Daily
                    </span>
                  ) : (
                    <span className="text-[10px] text-white/35 uppercase">
                      {(t.kind || '').replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
                {isDaily && (
                  <div className="text-[11px] text-white/60 truncate">
                    Assigned by: <span className="text-white/90 font-medium">{assignedByText}</span>
                  </div>
                )}
                {t.taskDate && isDaily && (
                  <div className="text-[10px] text-white/45">
                    For: {new Date(t.taskDate).toLocaleDateString()}
                  </div>
                )}
                {(t.deadline || t.dueAt) && (
                  <div className="text-[10px] text-[#ff8ec5]/80">
                    Due {new Date(t.deadline || t.dueAt).toLocaleDateString()}
                  </div>
                )}
              </button>
            );
          }}
          onMove={(id, _f, to) =>
            run(async () => {
              const task = tasks.find((t) => t.id === id);
              if (task) await moveFounder(task, to);
            })
          }
        />
      )}

      {view === 'list' && (
        <div className="space-y-3">
          {filteredTasks.length === 0 && <p className="text-white/40">No tasks found.</p>}
          {filteredTasks.map((t) => {
            const isDaily = t.kind === 'daily' || t.isDaily;
            const assignedByText =
              t.assignedByName ||
              t.assignedBy?.name ||
              (t.assignedById ? 'Administrator' : 'Not recorded');

            return (
              <div
                key={t.id}
                className="rounded-xl border border-white/10 px-4 py-3 space-y-2 cursor-pointer hover:border-[#d42b92]/40"
                onClick={() => setActiveTaskId(t.id)}
                onKeyDown={(e) => e.key === 'Enter' && setActiveTaskId(t.id)}
                role="button"
                tabIndex={0}
              >
                <div className="flex justify-between gap-2">
                  <div>
                    <div className="text-white font-medium flex items-center gap-2 flex-wrap">
                      {t.code ? <span className="text-white/40">{t.code} ·</span> : null}
                      <span>{t.title}</span>
                      {isDaily && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#d42b92]/20 text-[#ff8ec5] border border-[#d42b92]/40">
                          Daily
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-white/40 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                      <span>{isDaily ? 'Daily task' : t.kind}</span>
                      {isDaily && (
                        <span>
                          · Assigned by: <strong className="text-white/80 font-normal">{assignedByText}</strong>
                        </span>
                      )}
                      {t.taskDate && isDaily && (
                        <span>· Date: {new Date(t.taskDate).toLocaleDateString()}</span>
                      )}
                      {t.deadline || t.dueAt
                        ? ` · due ${new Date(t.deadline || t.dueAt).toLocaleDateString()}`
                        : ''}
                    </div>
                    {t.description && <p className="text-sm text-white/50 mt-1">{t.description}</p>}
                  </div>
                  {statusChip(t.status)}
                </div>

          {t.meeting && (
            <div className="text-xs text-white/50">
              Meeting {t.meeting.mode}
              {t.meeting.scheduledAt
                ? ` · ${new Date(t.meeting.scheduledAt).toLocaleString()}`
                : ''}
              {t.meeting.meetingLink ? (
                <>
                  {' '}
                  ·{' '}
                  <a
                    href={t.meeting.meetingLink}
                    className="text-[#ff8ec5]"
                    target="_blank"
                    rel="noreferrer"
                  >
                    join
                  </a>
                </>
              ) : null}
            </div>
          )}

          {t.sentDocument && (
            <button
              type="button"
              className="text-xs text-[#ff8ec5]"
              onClick={() =>
                downloadAuthed(
                  portalApi.downloadBatchSentDocUrl(t.sentDocument.id),
                  t.sentDocument.originalName || t.sentDocument.title
                )
              }
            >
              Download: {t.sentDocument.title}
            </button>
          )}

          {t.documentRequest?.submittedUrl && (
            <a
              href={resolveFileUrl(t.documentRequest.submittedUrl)}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-[#ff8ec5]"
              onClick={(e) => e.stopPropagation()}
            >
              Your upload: {t.documentRequest.submittedOriginalName || 'Open file'}
            </a>
          )}

          {t.documentRequest && ['requested', 'rejected'].includes(t.documentRequest.status) && (
            <div className="space-y-2">
              <div className="text-xs text-white/50">
                Upload: {t.documentRequest.title} · {t.documentRequest.status}
              </div>
              {t.documentRequest.requiresSignature && (
                <label className="flex items-center gap-2 text-xs text-white/60">
                  <input
                    type="checkbox"
                    checked={!!signed[t.id]}
                    onChange={(e) => setSigned({ ...signed, [t.id]: e.target.checked })}
                  />
                  Signed
                </label>
              )}
              <input
                type="file"
                className="text-xs text-white/50"
                onChange={(e) =>
                  run(async () => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (t.documentRequest.requiresSignature && !signed[t.id]) {
                      throw new Error('Confirm signature first');
                    }
                    const fd = new FormData();
                    fd.append('file', file);
                    if (t.documentRequest.requiresSignature) fd.append('signed', 'true');
                    await portalApi.submitBatchDocRequest(t.id, fd);
                  })
                }
              />
            </div>
          )}

          {t.reviewFeedback && (
            <p className="text-xs text-amber-300/90">Feedback: {t.reviewFeedback}</p>
          )}

          <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
            {t.status === 'todo' && (
              <button
                type="button"
                onClick={() => run(() => portalApi.transitionTask(t.id, 'in_progress'))}
                className="text-xs text-[#ff8ec5]"
              >
                Start
              </button>
            )}
            {t.status === 'todo' && t.kind === 'milestone' && (
              <button
                type="button"
                onClick={() => run(() => portalApi.transitionTask(t.id, 'completed'))}
                className="text-xs text-[#ff8ec5]"
              >
                Complete
              </button>
            )}
            {t.status === 'in_progress' && (
              <>
                <button
                  type="button"
                  onClick={() => run(() => portalApi.transitionTask(t.id, 'ready_for_review'))}
                  className="text-xs text-[#ff8ec5]"
                >
                  Ready for review
                </button>
                <button
                  type="button"
                  onClick={() =>
                    run(() =>
                      portalApi.transitionTask(t.id, 'on_hold', window.prompt('Reason?') || 'hold')
                    )
                  }
                  className="text-xs text-white/50"
                >
                  Hold
                </button>
                {t.kind === 'milestone' && (
                  <button
                    type="button"
                    onClick={() => run(() => portalApi.transitionTask(t.id, 'completed'))}
                    className="text-xs text-[#ff8ec5]"
                  >
                    Complete
                  </button>
                )}
              </>
            )}
            {['clarification', 'blocked', 'on_hold'].includes(t.status) && (
              <button
                type="button"
                onClick={() => run(() => portalApi.transitionTask(t.id, 'in_progress'))}
                className="text-xs text-[#ff8ec5]"
              >
                Resume
              </button>
            )}
            {t.status === 'rework' && (
              <button
                type="button"
                onClick={() => run(() => portalApi.transitionTask(t.id, 'in_progress'))}
                className="text-xs text-[#ff8ec5]"
              >
                Resume after rework
              </button>
            )}
          </div>
        </div>
      );
    })}
      </div>
    )}

      {activeTaskId && (
        <BatchTaskDetailPanel
          taskId={activeTaskId}
          isAdmin={false}
          onClose={() => setActiveTaskId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

export function FounderMeetingsPage() {
  const [meetings, setMeetings] = useState([]);
  useEffect(() => {
    portalApi.myMeetings().then(setMeetings).catch(() => setMeetings([]));
  }, []);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-white">Meetings</h2>
      <p className="text-white/50 text-sm">Onboarding and active batch meetings.</p>
      {meetings.length === 0 && <p className="text-white/40">No meetings scheduled.</p>}
      {meetings.map((m) => {
        const gcal = googleCalHref(m);
        const ics = icsHref(m);
        return (
          <div
            key={`${m.source}-${m.id}`}
            className="rounded-xl border border-white/10 px-4 py-3 space-y-1"
          >
            <div className="flex justify-between gap-2">
              <div className="text-white font-medium">{m.title}</div>
              <span className="text-[10px] uppercase text-white/40">{m.source}</span>
            </div>
            <div className="text-sm text-white/50">
              {m.scheduledAt ? new Date(m.scheduledAt).toLocaleString() : 'Time TBD'}
              {m.mode ? ` · ${m.mode}` : ''}
              {m.stepTitle ? ` · ${m.stepTitle}` : ''}
            </div>
            <div className="flex flex-wrap gap-3 text-xs">
              {m.meetingLink && (
                <a href={m.meetingLink} className="text-[#ff8ec5]" target="_blank" rel="noreferrer">
                  Join
                </a>
              )}
              {gcal && (
                <a href={gcal} target="_blank" rel="noreferrer" className="text-[#ff8ec5]">
                  Google Calendar
                </a>
              )}
              {ics && (
                <a href={ics} download={`${m.title || 'meeting'}.ics`} className="text-[#ff8ec5]">
                  .ics
                </a>
              )}
            </div>
            {m.mom ? (
              <div className="mt-2 text-sm text-white/65 bg-black/30 rounded-lg p-2">
                <span className="text-white/40 text-xs uppercase block mb-1">Minutes of meeting</span>
                <p className="whitespace-pre-wrap">{m.mom}</p>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function FounderProfilePage() {
  const { user, reload } = useAuth();
  const [fullName, setFullName] = useState(user?.fullName || '');
  const [phone, setPhone] = useState(user?.phoneE164 || '');
  const [msg, setMsg] = useState('');

  const save = async (e) => {
    e.preventDefault();
    await portalApi.patchMe({ fullName, phoneE164: phone });
    await reload();
    setMsg('Saved');
  };

  return (
    <div className="space-y-6 max-w-md">
      <h2 className="text-2xl font-semibold text-white">Profile</h2>
      {msg && <p className="text-[#ff8ec5] text-sm">{msg}</p>}
      <form onSubmit={save} className="space-y-4">
        <input
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2 text-white"
          placeholder="Full name"
        />
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2 text-white"
          placeholder="Phone"
        />
        <button className="px-4 py-2 rounded-xl bg-[#d42b92] text-white text-sm">Save</button>
      </form>
    </div>
  );
}

export function FounderSettingsPage() {
  const { reload } = useAuth();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const save = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const data = await portalApi.changePassword(currentPassword || undefined, newPassword);
      if (data.accessToken) localStorage.setItem('innovher_token', data.accessToken);
      await reload();
      setMsg('Password updated');
      setCurrent('');
      setNew('');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="space-y-6 max-w-md">
      <h2 className="text-2xl font-semibold text-white">Settings</h2>
      <form onSubmit={save} className="space-y-4">
        <input
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrent(e.target.value)}
          placeholder="Current password (if known)"
          className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2 text-white"
        />
        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNew(e.target.value)}
          required
          minLength={6}
          placeholder="New password"
          className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2 text-white"
        />
        {error && <p className="text-red-400 text-sm">{error}</p>}
        {msg && <p className="text-[#ff8ec5] text-sm">{msg}</p>}
        <button className="px-4 py-2 rounded-xl bg-[#d42b92] text-white text-sm">Change password</button>
      </form>
    </div>
  );
}

export function NotificationsPage() {
  const [items, setItems] = useState([]);
  const load = () => portalApi.notifications().then(setItems).catch(() => setItems([]));
  useEffect(() => {
    load();
  }, []);

  return (
    <div className="min-h-screen bg-[#08080a] text-white">
      <div className="space-y-6 max-w-xl mx-auto p-6 pt-16">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-semibold">Notifications</h2>
          <button
            onClick={() => portalApi.readAllNotifications().then(load)}
            className="text-sm text-[#ff8ec5]"
          >
            Mark all read
          </button>
        </div>
        {items.length === 0 && <p className="text-white/40">Inbox empty.</p>}
        {items.map((n) => (
          <button
            key={n.id}
            onClick={() => portalApi.readNotification(n.id).then(load)}
            className={`w-full text-left rounded-xl border px-4 py-3 ${
              n.readAt ? 'border-white/5 text-white/50' : 'border-white/15 text-white'
            }`}
          >
            <div className="font-medium">{n.title}</div>
            <div className="text-sm opacity-70">{n.body}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function FounderDocumentsPage() {
  const { user } = useAuth();
  const startup = user?.startups?.[0];
  const startupId = startup?.id || startup?._id || user?.startupId;

  if (!startupId) {
    return (
      <div className="rounded-2xl border border-white/10 p-8 text-center text-white/50">
        No startup associated with your founder account.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SearchableStartupDocuments
        startupId={startupId}
        title={`${startup?.name || 'Startup'} · Documents Repository`}
      />
    </div>
  );
}
